import { css, cx } from '@emotion/css';
import { Box } from '@mui/material';
import { ReactComponent as CheckIcon } from '@/assets/icons/checkIcon.svg';
import { ReactComponent as MinusIcon } from '@/assets/icons/minusIcon.svg';
import { styled } from '@mui/material/styles';
import MuiSwitch, { SwitchProps } from '@mui/material/Switch';
import colors from '@/theme/colors';

const styles = {
  switch: css`
    height: 16px;
    width: 16px;
    border-radius: 50%;
    display: flex;
    display: flex;
    align-items: center;
    justify-content: center;
  `,
  checked: css`
    color: ${colors.white};
    background-color: ${colors.white};
  `,
  unchecked: css`
    background-color: ${colors.neutralAsh};
    color: ${colors.white};
  `,
};

function SwitchThumb(props) {
  return (
    <div className={cx(styles.switch, props.isChecked ? styles.checked : styles.unchecked)}>
      {props.isChecked ? <CheckIcon /> : <MinusIcon />}
    </div>
  );
}

const IOSSwitch = styled(MuiSwitch)(({ theme }) => ({
  width: 36,
  height: 20,
  padding: 0,
  '& .MuiSwitch-switchBase': {
    padding: 2,
    transitionDuration: '300ms',
    '&.Mui-checked': {
      transform: 'translateX(16px)',
      color: '#fff',
      '& .MuiSwitch-thumb': {
        backgroundColor: `${colors.white}`,
      },
      '& + .MuiSwitch-track': {
        backgroundColor: `${colors.jungle}`,
        borderColor: `${colors.jungle}`,
        opacity: 1,
        border: 0,
      },
      '&.Mui-disabled + .MuiSwitch-track': {
        opacity: 0.5,
      },
    },
    '& .MuiSwitch-thumb': {
      backgroundColor: `${colors.neutralAsh}`,
    },
    '&.Mui-focusVisible .MuiSwitch-thumb': {
      color: `${colors.jungle}`,
      border: `2px solid ${colors.focusOutlineColor}`,
    },
    '&.Mui-disabled .MuiSwitch-thumb': {
      color: theme.palette.grey[400],
    },
    '&.Mui-disabled + .MuiSwitch-track': {
      opacity: 0.7,
    },
  },
  '& .MuiSwitch-thumb': {
    boxSizing: 'border-box',
    width: 16,
    height: 16,
    backgroundColor: `${colors.neutralAsh}`,
  },
  '& .MuiSwitch-track': {
    borderRadius: 13,
    padding: 2,
    backgroundColor: `${colors.silver}`,
    opacity: 1,
    border: `1px solid ${colors.neutralAsh}`,
    transition: theme.transitions.create(['background-color', 'border'], {
      duration: 500,
    }),
  },
}));

interface SwitchPropsWithId extends SwitchProps {
  id: string;
  ariaLabel?: string;
}
export const Switch = ({ id, checked, onChange, ariaLabel }: SwitchPropsWithId) => {
  const toggle = () => {
    const newChecked = !checked;
    const syntheticEvent = {
      target: { checked: newChecked },
    } as React.ChangeEvent<HTMLInputElement>;
    onChange?.(syntheticEvent, newChecked);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === ' ' || e.key === 'Enter') {
      e.preventDefault();
      toggle();
    }
  };

  return (
    <Box
      component="span"
      id={id}
      role="switch"
      tabIndex={0}
      aria-checked={checked}
      aria-label={ariaLabel}
      onKeyDown={handleKeyDown}
      onClick={e => {
        e.stopPropagation();
        toggle();
      }}
      sx={{
        display: 'inline-flex',
        cursor: 'pointer',
        '&:focus-visible': {
          outline: `2px solid ${colors.focusOutlineColor}`,
          outlineOffset: '2px',
          borderRadius: '4px',
        },
      }}
    >
      <IOSSwitch
        icon={<SwitchThumb isChecked={false} />}
        checkedIcon={<SwitchThumb isChecked={true} />}
        checked={checked}
        onChange={onChange}
        tabIndex={-1}
        inputProps={{
          tabIndex: -1,
          'aria-hidden': true,
        }}
        sx={{ pointerEvents: 'none' }}
      />
    </Box>
  );
};
